import { Badge, Select } from '@radix-ui/themes';
import { X } from 'lucide-react';
import { useState } from 'react';

type Option = {
  label: string;
  value: string;
};

interface MultiSelectProps {
  options: Option[];
  onChange?: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  preSelected?: string[];
}

const MultiSelect = ({ options, onChange, placeholder = 'Select...', className, preSelected }: MultiSelectProps) => {
  const [selected, setSelected] = useState<string[]>(preSelected ?? []);

  const handleSelect: Select.RootProps['onValueChange'] = (value) => {
    const newSelected = [...selected, value];
    setSelected(newSelected);
    onChange?.(newSelected);
  };

  const handleRemove = (value: string) => {
    const newSelected = selected.filter((v) => v !== value);
    setSelected(newSelected);
    onChange?.(newSelected);
  };

  return (
    <div className={className}>
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '6px' }}>
          {selected.map((value, index) => (
            <Badge key={index} variant="soft" color="blue" radius="large">
              {options.find((option) => option.value === value)?.label}
              <X size={14} className="clickable" style={{ marginLeft: 2 }} onClick={() => handleRemove(value)} />
            </Badge>
          ))}
        </div>
      )}
      <Select.Root size="3" value="" onValueChange={handleSelect}>
        <Select.Trigger placeholder={placeholder} style={{ width: '100%' }} />
        <Select.Content position="popper">
          {(() => {
            const remaining = options.filter((option) => !selected.includes(option.value));
            if (remaining.length === 0) {
              return (
                <div style={{ padding: '8px 12px', color: 'var(--gray-a10)', fontSize: 'var(--font-size-2)' }}>
                  All options selected
                </div>
              );
            }
            return remaining.map((option) => (
              <Select.Item key={option.value} value={option.value}>
                {option.label}
              </Select.Item>
            ));
          })()}
        </Select.Content>
      </Select.Root>
    </div>
  );
};

export default MultiSelect;
